import React, { useState } from 'react';
import { useReduxLeads } from '@Hooks/reduxLeadsHook/useReduxLeads';
import {
    ChevronDown,
    CheckCircle2,
    AlertTriangle,
    CircleCheck,
    TriangleAlert,
    CircleX,
    Clock3
} from 'lucide-react';
import { Tooltip } from '@mui/material';
import './WhatsappHeader.scss';

const WhatsappHeader = () => {

    const [isOpen, setIsOpen] = useState(false);
    const { setCurrentLeads, leadsList, numbersList, setPhoneNumber, currentPhoneNumber } = useReduxLeads();

    const handleNumberChange = (number) => {
        setPhoneNumber(number);
        setIsOpen(false);
    };

    if (!currentPhoneNumber) {
        return (
            <div className="whatsapp-switcher whatsapp-switcher--empty">
                <div className="whatsapp-switcher__logo">
                    <img
                        src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
                        alt="WhatsApp"
                    />
                </div>

                <div className="whatsapp-switcher__empty-text">
                    <span>WhatsApp</span>
                    <small>No number selected</small>
                </div>
            </div>
        );
    }

    const getPhoneNumberQuality = (quality) => {
        switch (quality) {
            case 'GREEN':
                return {
                    label: 'High',
                    icon: <CircleCheck size={13} />,
                    className: 'high',
                };

            case 'YELLOW':
                return {
                    label: 'Medium',
                    icon: <TriangleAlert size={13} />,
                    className: 'medium',
                };

            case 'RED':
                return {
                    label: 'Low',
                    icon: <CircleX size={13} />,
                    className: 'low',
                };

            default:
                return {
                    label: 'Pending',
                    icon: <Clock3 size={13} />,
                    className: 'pending',
                };
        }
    };

    return (
        <div className="whatsapp-switcher">

            <div className="whatsapp-switcher__logo">
                <img
                    src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
                    alt="WhatsApp"
                />
            </div>

            <div className="whatsapp-switcher__current">

                <div className="whatsapp-switcher__title-row">

                    <span className="whatsapp-switcher__name">
                        {currentPhoneNumber.verified_name || 'WhatsApp'}
                    </span>

                    {currentPhoneNumber.platform_type === 'CLOUD_API' && (
                        <Tooltip title="Connected" placement="top">
                            <CheckCircle2
                                size={14}
                                className="whatsapp-switcher__connected"
                            />
                        </Tooltip>
                    )}
                </div>

                <div className="whatsapp-switcher__sub-row">
                    <span className="whatsapp-switcher__phone">
                        {currentPhoneNumber.display_phone_number}
                    </span>
                </div>
            </div>

            {numbersList.length > 0 && (
                <div className="whatsapp-switcher__selector">

                    <button
                        type="button"
                        className={`whatsapp-switcher__trigger ${
                            isOpen ? 'is-open' : ''
                        }`}
                        onClick={() => setIsOpen(prev => !prev)}
                        aria-label="Switch WhatsApp number"
                    >
                        <ChevronDown
                            size={16}
                            className={`whatsapp-switcher__chevron ${
                                isOpen ? 'rotate' : ''
                            }`}
                        />
                    </button>

                    {isOpen && (
                        <>
                            <div
                                className="whatsapp-switcher__backdrop"
                                onClick={() => setIsOpen(false)}
                            />

                            <div className="whatsapp-switcher__dropdown">

                                {/* Dropdown heading */}
                                <div className="whatsapp-switcher__dropdown-header">
                                    <div>
                                        <span className="whatsapp-switcher__dropdown-title">
                                            WhatsApp Numbers
                                        </span>

                                        <span className="whatsapp-switcher__dropdown-description">
                                            Switch your active number
                                        </span>
                                    </div>

                                    <span className="whatsapp-switcher__count">
                                        {numbersList.length}
                                    </span>
                                </div>

                                {/* Number list */}
                                <div className="whatsapp-switcher__list">

                                    {numbersList.map((number) => {

                                        const selected = currentPhoneNumber?.id === number.id;
                                        const connected = number.platform_type === 'CLOUD_API';
                                        const quality = getPhoneNumberQuality(number?.quality_rating);

                                        return (
                                            <button
                                                type="button"
                                                key={number.id}
                                                className={`whatsapp-switcher__item ${
                                                    selected ? 'selected' : ''
                                                }`}
                                                onClick={() =>
                                                    handleNumberChange(number)
                                                }
                                            >

                                                {/* Icon */}
                                                <div
                                                    className={`whatsapp-switcher__item-icon ${
                                                        selected ? 'active' : ''
                                                    }`}
                                                >
                                                    <img
                                                        src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
                                                        alt=""
                                                    />
                                                </div>

                                                <div className="whatsapp-switcher__item-content">

                                                    <div className="whatsapp-switcher__item-name-row">

                                                        <span className="whatsapp-switcher__item-name">
                                                            {number.verified_name ||
                                                                'WhatsApp'}
                                                        </span>

                                                        {selected && (
                                                            <span className="whatsapp-switcher__active-label">
                                                                Active
                                                            </span>
                                                        )}

                                                    </div>

                                                    <span className="whatsapp-switcher__item-phone">
                                                        {number.display_phone_number}
                                                    </span>

                                                    <div className="whatsapp-switcher__item-status">

                                                        {connected ? (
                                                            <span className="status status--connected">
                                                                <CheckCircle2 size={11} />
                                                                Connected
                                                            </span>
                                                        ) : (
                                                            <span className="status status--warning">
                                                                <AlertTriangle size={11} />
                                                                Registration required
                                                            </span>
                                                        )}

                                                    </div>

                                                </div>
                                                <span
                                                    className={`whatsapp-switcher__quality whatsapp-switcher__quality--${quality.className}`}>
                                                    {quality.icon}
                                                    {quality.label}
                                                </span>
                                            </button>
                                        );
                                    })}

                                </div>
                            </div>
                        </>
                    )}

                </div>
            )}

        </div>
    );
};

export default WhatsappHeader;
